import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Fade,
  Grow
} from '@mui/material';
import {
  SelfImprovement,
  EditNote,
  EmojiEvents,
  TrendingUp,
  PlayArrow,
  Pause
} from '@mui/icons-material';

const BurnoutLandingPage = () => {
  const navigate = useNavigate();
  const [breathingActive, setBreathingActive] = useState(false);
  const [breathingPhase, setBreathingPhase] = useState('inhale');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    let interval;
    if (breathingActive) {
      interval = setInterval(() => {
        setBreathingPhase(prev => prev === 'inhale' ? 'exhale' : 'inhale');
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [breathingActive]);

  const handleGetStarted = () => {
    navigate('/login?mode=signup');
  };

  const toggleBreathing = () => {
    setBreathingActive(!breathingActive);
  };

  const features = [
    {
      icon: <SelfImprovement sx={{ fontSize: 40, color: '#667eea' }} />,
      title: 'Guided Meditations',
      description: 'Daily mindfulness practices tailored to your stress level'
    },
    {
      icon: <EditNote sx={{ fontSize: 40, color: '#764ba2' }} />,
      title: 'Reflective Journaling',
      description: 'Process your thoughts and track emotional patterns'
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 40, color: '#4ECDC4' }} />,
      title: 'Quick Challenges',
      description: '5-minute activities that build resilience and focus'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: '#FF6B6B' }} />,
      title: 'Progress Tracking',
      description: 'See your improvement with data-driven insights'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Floating particles effect */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '200px',
            height: '200px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            top: '10%',
            left: '10%',
            animation: 'float 6s ease-in-out infinite'
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: '150px',
            height: '150px',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
            bottom: '20%',
            right: '15%',
            animation: 'float 8s ease-in-out infinite reverse'
          },
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' }
          }
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Fade in={isVisible} timeout={1000}>
                <Box>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '4rem' },
                      fontWeight: 800,
                      color: '#ffffff',
                      mb: 3,
                      textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                      lineHeight: 1.1
                    }}
                  >
                    From Burnout to{' '}
                    <Box component="span" sx={{ 
                      color: '#4ECDC4',
                      textShadow: '0 0 20px rgba(78, 205, 196, 0.5)'
                    }}>
                      Breakthrough
                    </Box>
                  </Typography>
                  
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ffffff',
                      mb: 4,
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    Your Daily Progress Starts Here
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      mb: 5,
                      lineHeight: 1.6,
                      fontSize: { xs: '1.1rem', md: '1.3rem' }
                    }}
                  >
                    Discover small, powerful practices that turn stress into strength. 
                    Track your journey and see real results.
                  </Typography>
                  
                  <Button 
                    variant="contained"
                    size="large" 
                    onClick={handleGetStarted}
                    sx={{
                      background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      padding: '16px 40px',
                      borderRadius: 50,
                      textTransform: 'none',
                      boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #FF5252, #26A69A)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 40px rgba(255, 107, 107, 0.4)'
                      }
                    }}
                  >
                    Start Your Progress Journey
                  </Button>
                  
                  <Box sx={{ mt: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {['Free to Start', '5 Minutes Daily', 'Proven Results'].map((text) => (
                      <Typography key={text} sx={{ 
                        color: 'rgba(255,255,255,0.9)', 
                        fontSize: '1rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        '&::before': {
                          content: '"✓"',
                          marginRight: '8px',
                          color: '#4ECDC4',
                          fontWeight: 'bold'
                        }
                      }}>
                        {text}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Fade>
            </Grid>

            {/* Interactive Breathing Exercise */}
            <Grid item xs={12} md={5}>
              <Grow in={isVisible} timeout={1500}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.9)', 
                      mb: 3,
                      fontWeight: 600
                    }}
                  >
                    Take a moment to breathe
                  </Typography>
                  
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box
                      sx={{
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.3)',
                        transition: 'all 4s ease-in-out',
                        transform: breathingActive 
                          ? (breathingPhase === 'inhale' ? 'scale(1.2)' : 'scale(0.9)')
                          : 'scale(1)',
                        boxShadow: breathingActive 
                          ? '0 0 40px rgba(78, 205, 196, 0.5)' 
                          : '0 0 20px rgba(255,255,255,0.2)'
                      }}
                    >
                      <IconButton
                        onClick={toggleBreathing}
                        sx={{
                          color: 'white',
                          fontSize: '3rem',
                          '&:hover': { background: 'rgba(255,255,255,0.1)' }
                        }}
                      >
                        {breathingActive ? <Pause sx={{ fontSize: 40 }} /> : <PlayArrow sx={{ fontSize: 40 }} />}
                      </IconButton>
                    </Box>
                    
                    {breathingActive && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          bottom: -40,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          color: 'rgba(255,255,255,0.9)',
                          fontWeight: 600,
                          fontSize: '1.1rem'
                        }}
                      >
                        {breathingPhase === 'inhale' ? 'Breathe In...' : 'Breathe Out...'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grow>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 10, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#2c3e50',
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Your Complete Wellness Toolkit
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              color: '#5a6c7d',
              mb: 6,
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Everything you need to transform burnout into breakthrough, 
            one small step at a time.
          </Typography>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in={isVisible} timeout={1000 + index * 200}>
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      background: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#2c3e50',
                          mb: 2
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#5a6c7d',
                          lineHeight: 1.6
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonial Section */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative'
      }}>
        <Container maxWidth="md">
          <Fade in={isVisible} timeout={2000}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  fontStyle: 'italic',
                  color: '#ffffff',
                  lineHeight: 1.6,
                  mb: 4,
                  fontSize: { xs: '1.3rem', md: '1.8rem' },
                  fontWeight: 300,
                  position: 'relative',
                  '&::before': {
                    content: '"',
                    fontSize: '4rem',
                    color: 'rgba(255,255,255,0.3)',
                    position: 'absolute',
                    top: -20,
                    left: -30
                  },
                  '&::after': {
                    content: '"',
                    fontSize: '4rem',
                    color: 'rgba(255,255,255,0.3)',
                    position: 'absolute',
                    bottom: -40,
                    right: -30
                  }
                }}
              >
                Burnout used to own me. I was forgetful, unfocused, and checked out. 
                Sanity Reps gave me small daily practices—meditations, journaling, quick challenges, 
                and a progress tracker that proved I was improving. Now I feel sharp, calm, and confident. 
                Burnout didn't win, progress did.
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#4ECDC4',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                — Sarah M., Marketing Manager
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box sx={{ 
        py: 10,
        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Fade in={isVisible} timeout={2500}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#ffffff',
                  mb: 3,
                  fontSize: { xs: '2rem', md: '2.8rem' }
                }}
              >
                Ready to Transform Your Days?
              </Typography>
              
              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mb: 5,
                  lineHeight: 1.6,
                  maxWidth: 500,
                  mx: 'auto'
                }}
              >
                Join thousands who've turned burnout into breakthrough. 
                Your progress journey starts with one click.
              </Typography>
              
              <Button 
                variant="contained"
                size="large" 
                onClick={handleGetStarted}
                sx={{
                  background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.3rem',
                  padding: '20px 50px',
                  borderRadius: 50,
                  textTransform: 'none',
                  boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FF5252, #26A69A)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 40px rgba(255, 107, 107, 0.4)'
                  }
                }}
              >
                Experience Your Transformation
              </Button>
              
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '1rem',
                  mt: 4,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 3,
                  flexWrap: 'wrap'
                }}
              >
                {['Free forever plan available', 'Start in 30 seconds', 'No credit card required'].map((text) => (
                  <Box key={text} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      background: '#4ECDC4', 
                      mr: 1 
                    }} />
                    {text}
                  </Box>
                ))}
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default BurnoutLandingPage;